// consent.js — GDPR/ePrivacy consent gate, geo-scoped to EEA + UK.
//
// Non-EEA/UK visitors (India, US, and the rest) are banner-free and track
// EXACTLY as before — this module resolves them to "open" immediately and never
// suppresses anything. For EEA/UK visitors we withhold the Meta Pixel init and
// ALL tracking (Meta client + our CAPI + GA) until they explicitly accept.
//
// The market is decided server-side (Cloudflare geo) and surfaced by
// GET /api/market-config as `needs_consent_banner` (true only for EEA/UK — see
// backend/market_profile.EEA_UK_COUNTRIES). GA cookies are additionally gated at
// the source by Google Consent Mode v2 (default-denied for these regions in
// public/index.html); this module grants that consent on accept.
import { fetchMarketConfig } from "@/utils/marketConfig";

const KEY = "pos_consent"; // persisted choice: "granted" | "denied"

// 'unknown'  — geo not resolved yet (initial paint)
// 'open'     — no consent needed (US/IN/rest) OR the visitor granted it
// 'blocked'  — EEA/UK visitor who has not granted → suppress tracking
let _state = "unknown";
const _subs = new Set();

function _notify() { _subs.forEach((f) => { try { f(_state); } catch { /* noop */ } }); }

export function subscribeConsent(fn) {
  _subs.add(fn);
  fn(_state);
  return () => _subs.delete(fn);
}

export function consentState() { return _state; }

// The single predicate every tracker checks. 'unknown' and 'open' allow (so
// US/IN never lose events); only a resolved EEA/UK-without-consent blocks.
export function trackingAllowed() { return _state !== "blocked"; }

function _stored() { try { return localStorage.getItem(KEY) || ""; } catch { return ""; } }
function _persist(v) { try { localStorage.setItem(KEY, v); } catch { /* storage off */ } }

function _grantGoogle() {
  if (typeof window !== "undefined" && typeof window.gtag === "function") {
    window.gtag("consent", "update", {
      ad_storage: "granted",
      analytics_storage: "granted",
      ad_user_data: "granted",
      ad_personalization: "granted",
    });
  }
}

function _open() {
  _state = "open";
  // Meta Pixel may init now (deferred out of index.html so no _fbp cookie is
  // set for an EEA/UK visitor before consent). No-op if already inited.
  import("@/utils/metaPixel")
    .then((m) => { if (m.initMetaPixel) m.initMetaPixel(); })
    .catch(() => { /* pixel util unavailable — nothing to init */ });
  _grantGoogle();
  _notify();
}

function _block() { _state = "blocked"; _notify(); }

// Resolve once per SPA load: does this visitor need a banner?
let _booted = false;
export async function initConsent() {
  if (_booted) return;
  _booted = true;
  let cfg = null;
  try { cfg = await fetchMarketConfig(); } catch { cfg = null; }
  // On any failure the India-safe FALLBACK_CONFIG has needs_consent_banner=false,
  // so India/US keep tracking; we only ever block on a POSITIVE EEA/UK signal.
  const required = !!(cfg && cfg.needs_consent_banner);
  if (!required) { _open(); return; }
  if (_stored() === "granted") { _open(); return; } // returning visitor accepted
  _block();                                          // EEA/UK: undecided or declined
}

// Banner shows only for an EEA/UK visitor who hasn't chosen yet.
export function needsBanner() { return _state === "blocked" && _stored() === ""; }

export function acceptConsent() { _persist("granted"); _open(); }
export function declineConsent() { _persist("denied"); _block(); }
